import React, { ChangeEvent, useCallback, useContext, useState } from 'react';
import AppContext from 'app/AppContext';
import PillInput from 'utils/components/PillInput/PillInput';
import { PortfolioPosition } from 'portfolio/PortfolioSelection';
import {
    useDispatchPositionEntrySet,
    useDispatchPositionEntryClear,
    useDispatchPositionEntryInputSet,
} from './store/dispatchers';
import {
    useSelectPositionEntryInput,
    useSelectPositionEntryPosition,
} from './store/selectors';
import {
    formatInputText,
    getValidPills,
    getFormattedPills,
    getPortfolioPosition,
    getSanitisedPills,
} from './pillValidation';
import './PositionEntry.scss';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useSymbolValidation } from 'symbols/hooks/useSymbolValidation';
import { WarningPanel } from 'utils/components/WarningPanel/WarningPanel';
import SymbolInput from 'symbols/components/SymbolInput';
import { isQueryParameterPresent } from '@instinet-analytics/core-lib';
const updateInputText = (inputText: string, confirmedPills: string[]) =>
    formatInputText(inputText, confirmedPills.length);

export default function PositionEntry() {
    const dispatchPositionEntryInputSet = useDispatchPositionEntryInputSet();
    const dispatchPositionEntrySet = useDispatchPositionEntrySet();
    const dispatchPositionEntryClear = useDispatchPositionEntryClear();

    const hide = isQueryParameterPresent('nosymoverride', ['1', 'true']);

    const dispatchPosition = useCallback(
        (position: PortfolioPosition | undefined) => {
            if (position) {
                dispatchPositionEntrySet(position);
            } else {
                dispatchPositionEntryClear();
            }
        },
        [dispatchPositionEntrySet, dispatchPositionEntryClear]
    );

    const { pills } = useSelectPositionEntryInput();
    const position = useSelectPositionEntryPosition();
    const { symbology } = useSelectAppSettings();
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const symbol = position?.symbol;

    const { symbolInvalid, result: symbolValidationResult } =
        useSymbolValidation(symbol, symbology);

    const onPillsChanged = useCallback(
        (pills: string[]) => {
            const validPills = getValidPills(pills);

            dispatchPositionEntryInputSet({
                pills: validPills,
            });

            dispatchPosition(getPortfolioPosition(validPills));
        },
        [dispatchPosition, dispatchPositionEntryInputSet]
    );

    const { numberFormat } = useContext(AppContext);

    const onConfirmInput = useCallback(() => {
        const validPills = getValidPills(pills);
        const sanitised = getSanitisedPills(validPills);

        dispatchPositionEntryInputSet({
            pills: getFormattedPills(sanitised, numberFormat),
        });

        dispatchPosition(getPortfolioPosition(sanitised));
    }, [pills, numberFormat, dispatchPosition, dispatchPositionEntryInputSet]);

    const onEditing = (editingIndex?: number) => {
        const nextShowValidationErrors = editingIndex === 1 || pills.length > 1;
        setShowValidationErrors(nextShowValidationErrors);
    };

    if (hide) {
        return <></>;
    }

    const placeholder = ['Symbol Override', 'Set Shares'];
    const renderInputs = [renderSymbolInput];
    return (
        <div className="PositionEntry">
            <PillInput
                pills={pills}
                updateInputText={updateInputText}
                onPillsChanged={onPillsChanged}
                onEnter={onConfirmInput}
                onTab={onConfirmInput}
                onInputLoseFocus={onConfirmInput}
                placeholder={placeholder}
                hideInputPillCount={placeholder.length}
                renderInputs={renderInputs}
                onEditing={onEditing}
                noInputTextOnBackspaceRemovePill
            />
            {showValidationErrors && symbolInvalid && (
                <WarningPanel
                    text={`${symbolValidationResult.symbol} is not a valid ${symbolValidationResult.symbology} symbol`}
                />
            )}
        </div>
    );

    type SymbolInputProps = React.DetailedHTMLProps<
        React.InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
    >;

    function renderSymbolInput(props: SymbolInputProps) {
        const { value, placeholder, onKeyDown, onBlur, onChange } = props;
        const symbol: string = value as string;
        const onSymbolChange = (nextValue: string) => {
            if (onChange) {
                const ev = {
                    target: { value: nextValue },
                } as ChangeEvent<HTMLInputElement>;
                onChange(ev);
            }
        };

        return (
            <SymbolInput
                value={symbol}
                onChange={onSymbolChange}
                suppressInvalidMessages={true}
                placeholder={placeholder}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
            />
        );
    }
}
